import { useEffect } from 'react'
import { useQuery, useMutation, useLazyQuery } from '@apollo/client/react'
import {
  GET_PROVIDERS,
  GET_ENGAGEMENT,
  ADD_PROVIDERS_TO_PIPELINE,
  UPDATE_PROVIDER_STATUS,
  REMOVE_PROVIDER,
  ENRICH_PROVIDER_EMAIL,
  ENRICH_PROVIDER_CMS,
  GENERATE_OUTREACH_EMAIL,
  GET_OUTREACH_RUN,
  CHECK_OUTREACH_COMPLIANCE,
  PUBLISH_OUTREACH_EMAIL,
  PREFETCH_PROVIDER_CMS,
  SEARCH_PROVIDERS_BY_NAME,
  UPDATE_PROVIDER_EMAIL,
  UPDATE_PROVIDER,
  SET_PROVIDERS_CAMPAIGN_SELECTION,
} from '@/lib/operations/providers'
import type {
  Provider,
  ProviderStatus,
  OutreachComplianceResult,
  OutreachEmailDraft,
  OutreachOverrides,
  ProvenanceSentence,
} from '@/lib/types'
import { useLangGraphStream } from '@/hooks/useLangGraphStream'
import { useOutreachStore } from '@/state/outreachStore'

// ─── Input types ────────────────────────────────────────────────────────────────────────────

export interface EngagementSignalInput {
  date: string
  signal_type: string
  detail?: string
}

// ─── Read hooks ─────────────────────────────────────────────────────────────────────────────

export function useProviders(clientId: string) {
  const { data, loading, error } = useQuery<{ providers: Provider[] }>(GET_PROVIDERS, {
    variables: { clientId },
    skip: !clientId,
  })
  return { providers: data?.providers ?? [], loading, error }
}

export function useEngagement(clientId: string) {
  const { data, loading, error } = useQuery<{ providerEngagement: Provider[] }>(GET_ENGAGEMENT, {
    variables: { clientId },
    skip: !clientId,
  })
  return { providers: data?.providerEngagement ?? [], loading, error }
}

export function useOutreachRun(clientId: string, runId: string | null) {
  const { data, loading, error, startPolling, stopPolling } = useQuery<{
    outreachRun: { runId: string; status: string; email: OutreachEmailDraft | null; error: string | null; nodeRuntimeData: unknown; completedNodes: string[]; progressPct: number; graphNodes: string[]; provenance: ProvenanceSentence[] }
  }>(GET_OUTREACH_RUN, {
    variables: { clientId, runId: runId ?? '' },
    skip: !clientId || !runId,
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    if (!runId) {
      stopPolling()
    }
  }, [runId, stopPolling])

  return { run: data?.outreachRun, loading, error, startPolling, stopPolling }
}

// ─── Mutation hooks ───────────────────────────────────────────────────────────────────────────

export function useAddProvidersToPipeline() {
  const [mutate, { loading }] = useMutation<{ addProvidersToPipeline: { added: number } }>(
    ADD_PROVIDERS_TO_PIPELINE,
  )
  const addProviders = (clientId: string, providers: Provider[]) =>
    mutate({
      variables: {
        clientId,
        providers: providers.map(p => ({
          _id: p._id,
          provider_name: p.provider_name,
          specialty: p.specialty,
          practice_name: p.practice_name,
          city: p.city,
          state: p.state,
          npi: p.npi,
          prescribing_volume_annual_usd: p.prescribing_volume_annual_usd ?? null,
          estimated_total_medicare_usd: p.estimated_total_medicare_usd ?? null,
          dme_product_mix: (p.dme_product_mix ?? []).map(({ __typename: _, ...dm }) => dm),
        })),
      },
      refetchQueries: [{ query: GET_PROVIDERS, variables: { clientId } }],
    })
  return { addProviders, loading }
}

export function useUpdateProviderStatus() {
  const [mutate, { loading }] = useMutation<{ updateProviderStatus: Provider }>(UPDATE_PROVIDER_STATUS)
  const updateStatus = (
    clientId: string,
    providerId: string,
    status: ProviderStatus,
    engagementSignal?: EngagementSignalInput,
    outreachEmail?: { subject: string; body: string; sent_at: string },
  ) =>
    mutate({
      variables: { clientId, providerId, status, engagementSignal, outreachEmail },
      refetchQueries: [
        { query: GET_PROVIDERS, variables: { clientId } },
        { query: GET_ENGAGEMENT, variables: { clientId } },
      ],
    })
  return { updateStatus, loading }
}

export function useRemoveProvider() {
  const [mutate, { loading }] = useMutation<{ removeProvider: boolean }>(REMOVE_PROVIDER)
  const removeProvider = (clientId: string, providerId: string) =>
    mutate({
      variables: { clientId, providerId },
      refetchQueries: [{ query: GET_PROVIDERS, variables: { clientId } }],
    })
  return { removeProvider, loading }
}

export interface DiscoveryProviderCms {
  npi: string
  prescribing_volume_annual_usd: number | null
  estimated_total_medicare_usd: number | null
  dme_product_mix: Array<{ product: string; volume_usd: number }>
  cms_rank_by_volume: number | null
  data_year: number | null
  cms_no_data: boolean
}

export function usePrefetchProviderCms() {
  const [mutate] = useMutation<{ prefetchProviderCms: DiscoveryProviderCms }>(PREFETCH_PROVIDER_CMS, { fetchPolicy: 'no-cache' })
  const prefetch = (npi: string) => mutate({ variables: { npi } })
  return { prefetch }
}

export function useEnrichProviderCms() {
  const [mutate, { loading }] = useMutation<{ enrichProviderCms: Provider }>(ENRICH_PROVIDER_CMS)
  const enrichCms = (clientId: string, providerId: string) =>
    mutate({ variables: { clientId, providerId } })
  return { enrichCms, loading }
}

export function useEnrichProviderEmail() {
  const [mutate, { loading }] = useMutation<{ enrichProviderEmail: Provider }>(ENRICH_PROVIDER_EMAIL)
  const enrichEmail = (clientId: string, providerId: string, useUserGemini?: boolean) =>
    mutate({ variables: { clientId, providerId, useUserGemini } })
  return { enrichEmail, loading }
}

export function useGenerateOutreachEmail() {
  const [mutate, { loading }] = useMutation<{ generateOutreachEmail: { runId: string } }>(
    GENERATE_OUTREACH_EMAIL,
  )
  const generateEmail = (clientId: string, providerId: string) =>
    mutate({ variables: { clientId, providerId } })
  return { generateEmail, loading }
}

export function useCheckCompliance() {
  const [mutate, { loading, data }] = useMutation<{
    checkOutreachCompliance: OutreachComplianceResult
  }>(CHECK_OUTREACH_COMPLIANCE)
  const checkCompliance = (
    clientId: string,
    providerId: string,
    subject: string,
    body: string,
  ) => mutate({ variables: { clientId, providerId, subject, body } })
  return { checkCompliance, loading, result: data?.checkOutreachCompliance ?? null }
}

export function usePublishOutreachEmail() {
  const [mutate, { loading }] = useMutation<{
    publishOutreachEmail: { id: string; status: string; createdAt: string }
  }>(PUBLISH_OUTREACH_EMAIL)
  const publishEmail = (
    clientId: string,
    providerId: string,
    subject: string,
    body: string,
    targetEmail?: string,
  ) => mutate({ variables: { clientId, providerId, subject, body, targetEmail } })
  return { publishEmail, loading }
}

// ─── Outreach SSE streaming ─────────────────────────────────────────────────────────────────────────

export function useStreamOutreachEmail() {
  const { stream: streamSSE, isStreaming, reset } = useLangGraphStream({
    onEvent: (type, data) => {
      const store = useOutreachStore.getState()

      if (type === 'node_update') {
        try {
          const parsed = JSON.parse(data) as {
            node?: string
            status?: string
            data?: {
              final_email?: { subject: string; body: string }
              provenance?: ProvenanceSentence[]
            }
          }
          
          if (parsed.node) {
            store.setNodeStatus(parsed.node, 'done')
            
            if (parsed.data?.final_email) {
              const { subject, body } = parsed.data.final_email
              store.setEmail({ subject, body })
            }
            
            if (Array.isArray(parsed.data?.provenance) && parsed.data.provenance.length > 0) {
              store.setProvenance(parsed.data.provenance)
            }
          }
        } catch {
          // Ignore malformed JSON
        }
        return
      }

      if (type === 'node_start' || type === 'node_end') {
        try {
          const parsed = JSON.parse(data) as { node?: string }
          if (parsed.node) {
            store.setNodeStatus(parsed.node, type === 'node_start' ? 'active' : 'done')
          }
        } catch {
          // Ignore malformed JSON
        }
        return
      }

      if (type === 'status') {
        try {
          const parsed = JSON.parse(data) as { run_id?: string }
          if (parsed.run_id) {
            store.setRunId(parsed.run_id)
          }
        } catch {
          // Ignore malformed JSON
        }
        return
      }
    },
    onDone: () => {
      useOutreachStore.getState().setAgentStatus('complete')
    },
    onError: (message) => {
      const store = useOutreachStore.getState()
      store.setAgentStatus('error')
      store.setErrorMessage(message)
    },
  })

  const stream = async (
    clientId: string,
    runId: string,
    providerId: string,
    overrides: OutreachOverrides = {},
  ) => {
    const store = useOutreachStore.getState()
    store.reset()
    store.setAgentStatus('running')

    const body: Record<string, unknown> = { runId, providerId }
    if (overrides.emailStyleOverride && overrides.emailStyleOverride !== 'automatic') body.emailStyleOverride = overrides.emailStyleOverride
    if (overrides.frameworkOverride && overrides.frameworkOverride !== 'automatic') body.frameworkOverride = overrides.frameworkOverride
    if (overrides.psychologyStrategy && overrides.psychologyStrategy !== 'automatic') body.psychologyStrategy = overrides.psychologyStrategy

    await streamSSE(
      `${import.meta.env.VITE_API_BASE_URL}/api/clients/${clientId}/outreach-stream`,
      body,
    )
  }

  return { stream, isStreaming, reset }
}

// ─── Name search hook ────────────────────────────────────────────────────────

export interface ProviderNameSearchResult {
  npi: string
  provider_name: string
  specialty: string
  practice_name: string
  city: string
  state: string
  address: string
  zip: string
  already_in_pipeline: boolean
}

export function useSearchProvidersByName() {
  const [search, { data, loading, error, called }] = useLazyQuery<{
    searchProvidersByName: ProviderNameSearchResult[]
  }>(SEARCH_PROVIDERS_BY_NAME, { fetchPolicy: 'network-only' })

  const searchByName = (clientId: string, name: string, enumerationType?: string, limit = 20) =>
    search({ variables: { clientId, name, enumerationType, limit } })

  return {
    searchByName,
    results: data?.searchProvidersByName ?? [],
    loading,
    error,
    called,
  }
}

// ─── Update provider email hook ───────────────────────────────────────────────

export function useUpdateProviderEmail() {
  const [mutate, { loading }] = useMutation<{ updateProviderEmail: boolean }>(UPDATE_PROVIDER_EMAIL)
  const updateProviderEmail = (clientId: string, providerId: string, email: string) =>
    mutate({ variables: { clientId, providerId, email } })
  return { updateProviderEmail, loading }
}

// ─── Update provider hook ─────────────────────────────────────────────────────

export interface UpdateProviderInput {
  provider_name?: string
  specialty?: string
  practice_name?: string
  city?: string
  state?: string
  zip?: string
  address?: string
  phone?: string
  fax?: string
  email?: string
  website_url?: string
  ehr_system?: string
  entity_type?: string
}

export function useUpdateProvider() {
  const [mutate, { loading }] = useMutation<{ updateProvider: Provider }>(UPDATE_PROVIDER)
  const updateProvider = (clientId: string, providerId: string, input: UpdateProviderInput) =>
    mutate({ variables: { clientId, providerId, input } })
  return { updateProvider, loading }
}

// ─── Set providers campaign selection hook ────────────────────────────────────

export function useSetProvidersCampaignSelection() {
  const [mutate, { loading }] = useMutation<{
    setProvidersCampaignSelection: Array<{ _id: string; selectedForCampaign: boolean }>
  }>(SET_PROVIDERS_CAMPAIGN_SELECTION)
  const setProvidersCampaignSelection = (clientId: string, providerIds: string[], selected: boolean) =>
    mutate({ variables: { clientId, providerIds, selected } })
  return { setProvidersCampaignSelection, loading }
}
